import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  featureSuggestionSchema,
  type FeatureSuggestionInput,
  type UserDto,
} from '@wingseat/shared';
import { useAuth } from '../auth/auth-context';
import { ApiError, api } from '../lib/api-client';
import {
  getCurrentSubscription,
  getPushDiagnostics,
  pushSupported,
  subscribeToPush,
  unsubscribeFromPush,
  type PushDiagnostics,
} from '../lib/push';

export function SettingsPage() {
  const { user, refetch } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [pushSubActive, setPushSubActive] = useState<boolean | null>(null);
  const [pushDiag, setPushDiag] = useState<PushDiagnostics | null>(null);

  useEffect(() => {
    if (!pushSupported()) {
      setPushSubActive(false);
      return;
    }
    getCurrentSubscription().then((s) => setPushSubActive(!!s));
  }, []);

  const settings = useQuery({
    queryKey: ['me', 'settings'],
    queryFn: () => api.get<UserDto>('/me/settings'),
  });

  const update = useMutation({
    mutationFn: (
      input: Partial<
        Pick<UserDto, 'pushEnabled' | 'emailNotificationsEnabled' | 'mobilePhone'>
      >,
    ) => api.patch<UserDto>('/me/settings', input),
    onSuccess: (data) => {
      queryClient.setQueryData(['me', 'settings'], data);
      refetch();
    },
  });

  const subscribePush = useMutation({
    mutationFn: async () => {
      await subscribeToPush();
    },
    onSuccess: () => {
      setPushSubActive(true);
      setPushDiag(null);
    },
    onError: () => {
      // Bei Push-Fehler Diagnose nachladen, damit der User die Werte sieht
      getPushDiagnostics().then(setPushDiag).catch(() => undefined);
    },
  });
  const unsubscribePush = useMutation({
    mutationFn: async () => {
      await unsubscribeFromPush();
    },
    onSuccess: () => setPushSubActive(false),
  });

  const logout = useMutation({
    mutationFn: async () => {
      await api.post('/auth/logout');
      await refetch();
      navigate('/login', { replace: true });
    },
  });

  const data = settings.data ?? user;

  return (
    <div className="px-4 py-6 space-y-6">
      <header>
        <h1 className="text-xl font-semibold">Einstellungen</h1>
      </header>

      {data && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs uppercase tracking-wide text-slate-500">
                Angemeldet als
              </p>
              <p className="mt-1 text-sm font-medium">
                {data.firstName} {data.lastName}
              </p>
              <p className="text-xs text-slate-400">{data.email}</p>
              <MobilePhoneRow
                current={data.mobilePhone}
                onSave={(value) => update.mutate({ mobilePhone: value })}
                isSaving={update.isPending}
              />
              <p className="mt-2 text-xs text-slate-400">
                Rolle: <span className="font-mono">{data.role}</span>
              </p>
            </div>
            <button
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
              className="shrink-0 self-start rounded-md border border-rose-900 bg-rose-950/40 px-2.5 py-1 text-xs font-medium text-rose-200 hover:bg-rose-900/60 disabled:opacity-60"
            >
              {logout.isPending ? '…' : 'Abmelden'}
            </button>
          </div>
        </div>
      )}

      <section className="space-y-3 rounded-xl border border-slate-800 bg-slate-900/60 p-4">
        <h2 className="text-sm font-semibold">Benachrichtigungen</h2>

        <ToggleRow
          label="E-Mail-Benachrichtigungen"
          checked={data?.emailNotificationsEnabled ?? false}
          disabled={update.isPending}
          onChange={(v) => update.mutate({ emailNotificationsEnabled: v })}
        />

        <ToggleRow
          label="Push-Benachrichtigungen erlaubt"
          description="Globale Erlaubnis. Muss zusätzlich auf diesem Gerät aktiviert werden."
          checked={data?.pushEnabled ?? false}
          disabled={update.isPending}
          onChange={(v) => update.mutate({ pushEnabled: v })}
        />

        <div className="border-t border-slate-800 pt-3">
          <p className="text-sm font-medium">Push auf diesem Gerät</p>
          {!pushSupported() ? (
            <p className="mt-1 text-xs text-slate-400">
              Auf diesem Gerät/Browser nicht verfügbar. iOS: nur in installierter
              PWA (ab iOS 16.4).
            </p>
          ) : pushSubActive === null ? (
            <p className="mt-1 text-xs text-slate-400">Prüfe Status …</p>
          ) : pushSubActive ? (
            <button
              onClick={() => unsubscribePush.mutate()}
              disabled={unsubscribePush.isPending}
              className="mt-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-xs hover:bg-slate-700 disabled:opacity-60"
            >
              {unsubscribePush.isPending ? 'Wird deaktiviert …' : 'Push hier deaktivieren'}
            </button>
          ) : (
            <button
              onClick={() => subscribePush.mutate()}
              disabled={subscribePush.isPending}
              className="mt-2 rounded-lg bg-brand-600 px-3 py-2 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-60"
            >
              {subscribePush.isPending ? 'Wird aktiviert …' : 'Push auf diesem Gerät aktivieren'}
            </button>
          )}
          {subscribePush.error && (
            <p className="mt-2 text-xs text-rose-400">
              {(subscribePush.error as Error).message}
            </p>
          )}
          {pushDiag && (
            <details className="mt-2 rounded-lg border border-slate-800 bg-slate-950/60 p-2 text-xs text-slate-400">
              <summary className="cursor-pointer text-slate-300">
                Push-Diagnose
              </summary>
              <ul className="mt-2 space-y-0.5 font-mono">
                <li>Notification.permission: <span className="text-slate-200">{pushDiag.permission}</span></li>
                <li>Push unterstuetzt: <span className="text-slate-200">{pushDiag.pushSupported ? 'ja' : 'nein'}</span></li>
                <li>Als PWA installiert: <span className="text-slate-200">{pushDiag.isPwa ? 'ja' : 'nein'}</span></li>
                <li>Service Worker: <span className="text-slate-200">{pushDiag.swState}</span></li>
                <li>Bestehende Subscription: <span className="text-slate-200">{pushDiag.hasExistingSubscription ? 'ja' : 'nein'}</span></li>
                <li>VAPID-Key gesetzt: <span className="text-slate-200">{pushDiag.vapidConfigured ? 'ja' : 'nein'}</span></li>
              </ul>
            </details>
          )}
        </div>
      </section>

      <FeatureSuggestionSection />
    </div>
  );
}

function FeatureSuggestionSection() {
  const [description, setDescription] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: async (input: FeatureSuggestionInput) => {
      await api.post('/me/feature-suggestion', input);
    },
    onSuccess: () => {
      setDescription('');
    },
  });

  const onSubmit = () => {
    const parsed = featureSuggestionSchema.safeParse({
      description: description.trim(),
    });
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? 'Ungültig');
      return;
    }
    setFieldError(null);
    submit.mutate(parsed.data);
  };

  const apiErr =
    submit.error instanceof ApiError
      ? submit.error.message
      : submit.error
        ? 'Senden fehlgeschlagen'
        : null;

  return (
    <section className="space-y-3 rounded-xl border border-slate-800 bg-slate-900/60 p-4">
      <h2 className="text-sm font-semibold">Funktion vorschlagen</h2>
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Funktion beschreiben"
        rows={3}
        disabled={submit.isPending}
        className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm disabled:opacity-60"
      />
      {fieldError && <p className="text-xs text-rose-400">{fieldError}</p>}
      {apiErr && <p className="text-xs text-rose-400">{apiErr}</p>}
      {submit.isSuccess && (
        <p className="text-xs text-emerald-300">
          Danke! Dein Vorschlag wurde versendet.
        </p>
      )}
      <button
        onClick={onSubmit}
        disabled={submit.isPending || !description.trim()}
        className="w-full rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-60"
      >
        {submit.isPending ? 'Sende …' : 'Einreichen'}
      </button>
    </section>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-5 w-5 rounded border-slate-600 bg-slate-950 accent-brand-600"
      />
      <span className="flex-1">
        <span className="block text-sm">{label}</span>
        {description && (
          <span className="mt-0.5 block text-xs text-slate-400">{description}</span>
        )}
      </span>
    </label>
  );
}

function MobilePhoneRow({
  current,
  onSave,
  isSaving,
}: {
  current: string | null;
  onSave: (value: string | null) => void;
  isSaving: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(current ?? '');

  useEffect(() => {
    if (!editing) setValue(current ?? '');
  }, [current, editing]);

  if (editing) {
    return (
      <div className="mt-2 space-y-2">
        <input
          type="tel"
          inputMode="tel"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="+49 151 14911566"
          className="w-full min-w-0 rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs"
          autoFocus
        />
        <div className="flex gap-2">
          <button
            onClick={() => {
              const trimmed = value.trim();
              onSave(trimmed === '' ? null : trimmed);
              setEditing(false);
            }}
            disabled={isSaving}
            className="rounded-md bg-brand-600 px-3 py-1.5 text-xs text-white hover:bg-brand-700 disabled:opacity-60"
          >
            Speichern
          </button>
          <button
            onClick={() => {
              setValue(current ?? '');
              setEditing(false);
            }}
            className="rounded-md border border-slate-700 px-3 py-1.5 text-xs"
          >
            Abbrechen
          </button>
        </div>
      </div>
    );
  }

  return (
    <p className="mt-1 flex items-center gap-2 text-xs text-slate-400">
      <span>{current ? current : <span className="italic text-slate-500">keine Mobilnummer</span>}</span>
      <button
        onClick={() => setEditing(true)}
        aria-label="Mobilnummer bearbeiten"
        title="Mobilnummer bearbeiten"
        className="rounded p-0.5 text-sm text-slate-400 hover:bg-slate-800 hover:text-brand-300"
      >
        ✏️
      </button>
    </p>
  );
}
